import { useAppSelector } from "@shared/store/hooks";
import Button from '@atlaskit/button/new';
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import MultiSelectInput, { MultiSelectOption } from "@shared/ui/MultiSelectInput";
import SimpleTreeMultiSelectInput, { TreeSelectSection } from "@shared/ui/SimpleTreeMultiSelectInput";
import { ReportsServices } from "@shared/api";
import { Box, Inline, Stack, Text } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import Textfield from "@atlaskit/textfield";
import { IoCloseOutline } from 'react-icons/io5';
import { ReportDateRangeField } from "./ReportDateRangeField";
import { adUnitTypeOptions, countryOptions } from "@shared/lib/constant";
import { subDays } from "date-fns";
import { createInitialFormState, dialogCardStyles, dialogOverlayStyles, dialogTitleStyles, dimensionOptions, fieldSectionStyles, footerButtonStyles, formatDateInput, formatDateLabel, metricOptions, NewReportDialogProps, parseDateInput, platformOptions, ReportFormState, sectionHeadingStyles } from "./helper";
import { userTypes } from "@shared/lib/access";


const parseDelimitedOptions = (
    value: string,
    options: MultiSelectOption[]
) => {
    const selectedValues = new Set(
        value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
    );

    return options.filter((option) => selectedValues.has(option.value));
};

export function NewReportDialog({ isOpen, onClose, report }: NewReportDialogProps) {
    const { activeApps } = useAppSelector((state) => state.inventory);
    const { demandPartnerProfiles, supplyPartnerProfiles } = useAppSelector((state) => state.partner);
    const {userType} = useAppSelector((state) => state.exchange);
    const [formState, setFormState] = useState<ReportFormState>(createInitialFormState);
    const [reportId, setReportId] = useState<string | null>(null);

    const appOptions = useMemo<MultiSelectOption[]>(
        () => activeApps.map((item) => ({ label: item.appName, value: item.appId })),
        [activeApps]
    );
    const supplyPartnerList = useMemo(
        () => Object.values(supplyPartnerProfiles),
        [supplyPartnerProfiles]
    );
    const demandPartnerList = useMemo(
        () => Object.values(demandPartnerProfiles),
        [demandPartnerProfiles]
    );

    useEffect(() => {
        if (isOpen) {
            if (!report) {
                setFormState(createInitialFormState());
                setReportId(null);
                return;
            }

            const nextAppId = parseDelimitedOptions(report.appId, appOptions);
            const nextCountry = parseDelimitedOptions(
                report.country,
                countryOptions.map((item) => ({ label: item.label, value: item.code }))
            );
            const nextAdUnitType = parseDelimitedOptions(report.adUnitType, adUnitTypeOptions);
            const nextPlatform = parseDelimitedOptions(report.platform, platformOptions);
            const nextDimensions = parseDelimitedOptions(report.dimensions, dimensionOptions);
            const nextMetrics = parseDelimitedOptions(report.metrics, metricOptions);
            const nextSpId = parseDelimitedOptions(
                report.spId,
                supplyPartnerList.map((item) => ({ label: item.spName, value: item.spId }))
            );
            const nextDpId = parseDelimitedOptions(
                report.dpId,
                demandPartnerList.map((item) => ({ label: item.dpName, value: item.dpId }))
            );

            setFormState({
                reportName: report.reportName,
                startDate: report.startDate,
                endDate: report.endDate,
                isHourly: report.dimensions.split(',').map((item) => item.trim()).includes('hour'),
                dimensions: nextDimensions.filter((option) => option.value !== 'hour' && option.value !== 'date'),
                metrics: nextMetrics,
                appId: nextAppId,
                country: nextCountry,
                adUnitType: nextAdUnitType,
                platform: nextPlatform,
                spId: nextSpId,
                dpId: nextDpId
            });
            setReportId(report.reportId);
        }
    }, [
        adUnitTypeOptions,
        appOptions,
        demandPartnerList,
        dimensionOptions,
        isOpen,
        metricOptions,
        report,
        supplyPartnerList
    ]);

    const supplyPartnerIdSet = useMemo(
        () => new Set(supplyPartnerList.map((item) => item.spId)),
        [supplyPartnerList]
    );
    const orgTreeSections = useMemo<TreeSelectSection[]>(
        () => [
            {
                label: 'Supply Partner',
                options: supplyPartnerList.map((item) => ({ label: item.spName, value: item.spId }))
            },
            {
                label: 'Demand Partner',
                options: demandPartnerList.map((item) => ({ label: item.dpName, value: item.dpId }))
            }
        ],
        [demandPartnerList, supplyPartnerList]
    );
    const selectedOrgIds = useMemo(
        () => [...formState.spId, ...formState.dpId],
        [formState.dpId, formState.spId]
    );
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;
    const today = new Date();
    const lastWeekStart = subDays(today, 6);

    const handleHourlyToggle = () => {
        setFormState((currentState) => {
            const nextIsHourly = !currentState.isHourly;

            if (!nextIsHourly) {
                return {
                    ...currentState,
                    isHourly: nextIsHourly
                };
            }

            const currentStartDate = parseDateInput(currentState.startDate);
            const currentEndDate = parseDateInput(currentState.endDate);
            const nextStartDate = currentStartDate < lastWeekStart ? lastWeekStart : currentStartDate;
            const clampedEndDate = currentEndDate > today ? today : currentEndDate;
            const nextEndDate = clampedEndDate < nextStartDate ? today : clampedEndDate;

            return {
                ...currentState,
                isHourly: nextIsHourly,
                startDate: formatDateInput(nextStartDate),
                endDate: formatDateInput(nextEndDate)
            };
        });
    };

    const triggerDownload = (url: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = async (buttonFlag: 0 | 1 | 2) => {
        const dimensions = formState.dimensions.map((option) => option.value);

        if (!dimensions.includes('date')) {
            dimensions.push('date');
        }

        if (formState.isHourly && !dimensions.includes('hour')) {
            dimensions.push('hour');
        }
        console.log(formState)

        const reportData = {
            reportName: formState.reportName,
            dpId: formState.dpId.map((option) => option.value).join(','),
            spId: formState.spId.map((option) => option.value).join(','),
            dimensions: dimensions.join(','),
            metrics: formState.metrics.map((option) => option.value).join(','),
            appId: formState.appId.map((option) => option.value).join(','),
            country: formState.country.map((option) => option.value).join(','),
            adUnitType: formState.adUnitType.map((option) => option.value).join(','),
            platform: formState.platform.map((option) => option.value).join(','),
            startDate: formState.startDate,
            endDate: formState.endDate
        };

        try {
            if (buttonFlag === 0) {
                if (reportId === null) {
                    const response = await ReportsServices.createAndSaveReport(reportData);
                    setReportId(response.reportId);
                    window.alert('Report created.');
                } else {
                    await ReportsServices.editAndSaveReport(reportId, reportData);
                    window.alert('Report edited.');
                }
                return;
            }

            if (buttonFlag === 1) {
                const response = await ReportsServices.downloadTempReport(reportData);
                window.alert('Report download started.');
                triggerDownload(response);
                return;
            }

            let nextReportId = reportId;

            if (nextReportId === null) {
                const response = await ReportsServices.createAndSaveReport(reportData);
                nextReportId = response.reportId;
                setReportId(response.reportId);
                window.alert('Report created and download started.');
            } else {
                await ReportsServices.editAndSaveReport(nextReportId, reportData);
                window.alert('Report edited and download started.');
            }

            const response = await ReportsServices.downloadReportById(nextReportId);
            triggerDownload(response);
        } catch (error) {
            console.error('Error submitting report:', error);
            window.alert('Please make sure that Report Name, Dimensions and Metrics are not empty.');
        }
    };

    if (!isOpen) {
        return null;
    }

    return createPortal(
        <Box style={dialogOverlayStyles}>
            <Box style={dialogCardStyles}>
                <Stack space="space.300">
                    <Inline alignBlock="center" spread="space-between">
                        <h2 style={dialogTitleStyles}>New Report</h2>

                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close new report dialog"
                            style={{
                                alignItems: 'center',
                                background: 'transparent',
                                border: 0,
                                color: token('color.icon.subtle'),
                                cursor: 'pointer',
                                display: 'inline-flex',
                                fontSize: '2.5rem',
                                justifyContent: 'center',
                                lineHeight: 1,
                                padding: 0
                            }}
                        >
                            <IoCloseOutline />
                        </button>
                    </Inline>

                    <Stack space="space.200">
                        <Textfield
                            aria-label="Report name"
                            placeholder="Report name"
                            value={formState.reportName}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                setFormState((currentState) => ({
                                    ...currentState,
                                    reportName: event.target.value
                                }))
                            }
                        />


                        <Inline alignBlock="center" spread="space-between">
                            <Text
                            // style={{
                            //   color: token('color.text.brand'),
                            //   fontSize: '1.75rem',
                            //   fontWeight: 500
                            // }}
                            >
                                {`${formatDateLabel(formState.startDate)} - ${formatDateLabel(formState.endDate)}`}
                            </Text>

                            <label
                                style={{
                                    alignItems: 'center',
                                    color: token('color.text'),
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    fontSize: '1.25rem',
                                    gap: '12px'
                                }}
                            >
                                <span style={{ fontWeight: 500, fontSize: '1rem' }}>Hourly</span>
                                <span
                                    style={{
                                        background: formState.isHourly
                                            ? token('color.background.success.bold')
                                            : token('color.background.neutral'),
                                        borderRadius: '999px',
                                        display: 'inline-flex',
                                        height: '20px',
                                        padding: '2px',
                                        position: 'relative',
                                        transition: 'background 0.2s ease',
                                        width: '45px'
                                    }}
                                >
                                    <input
                                        checked={formState.isHourly}
                                        onChange={handleHourlyToggle}
                                        style={{ display: 'none' }}
                                        type="checkbox"
                                    />
                                    <span
                                        style={{
                                            background: token('elevation.surface.overlay'),
                                            borderRadius: '50%',
                                            boxShadow: 'var(--app-shadow-card)',
                                            height: '16px',
                                            transform: formState.isHourly ? 'translateX(25px)' : 'translateX(0)',
                                            transition: 'transform 0.2s ease',
                                            width: '16px'
                                        }}
                                    />
                                </span>
                            </label>
                        </Inline>

                        <ReportDateRangeField
                            endDate={formState.endDate}
                            isHourly={formState.isHourly}
                            onChange={({ startDate, endDate }) =>
                                setFormState((currentState) => ({
                                    ...currentState,
                                    startDate,
                                    endDate
                                }))
                            }
                            startDate={formState.startDate}
                        />
                    </Stack>

                    <Box style={fieldSectionStyles}>
                        <Stack space="space.200">
                            <Inline space="space.200" shouldWrap>
                                <Box style={{ flex: '1 1 360px' }}>
                                    <MultiSelectInput
                                        ariaLabel="Dimensions"
                                        inputId="report-dimensions"
                                        isClearable
                                        label="Dimensions"
                                        menuPortalTarget={menuPortalTarget}
                                        onChange={(selectedOptions) =>
                                            setFormState((currentState) => ({
                                                ...currentState,
                                                dimensions: selectedOptions
                                            }))
                                        }
                                        options={dimensionOptions}
                                        placeholder="Select dimensions"
                                        value={formState.dimensions}
                                    />
                                </Box>

                                <Box style={{ flex: '1 1 360px' }}>
                                    <MultiSelectInput
                                        ariaLabel="Metrics"
                                        inputId="report-metrics"
                                        isClearable
                                        label="Metrics"
                                        menuPortalTarget={menuPortalTarget}
                                        onChange={(selectedOptions) =>
                                            setFormState((currentState) => ({
                                                ...currentState,
                                                metrics: selectedOptions
                                            }))
                                        }
                                        options={metricOptions}
                                        placeholder="Select metrics"
                                        value={formState.metrics}
                                    />
                                </Box>
                            </Inline>

                            <h3 style={sectionHeadingStyles}>Filters</h3>

                            <Inline space="space.200" shouldWrap>
                                <Box style={{ flex: '1 1 360px' }}>
                                    <MultiSelectInput
                                        ariaLabel="App Name"
                                        inputId="report-apps"
                                        isClearable
                                        label="App Name"
                                        menuPortalTarget={menuPortalTarget}
                                        onChange={(selectedOptions) =>
                                            setFormState((currentState) => ({
                                                ...currentState,
                                                appId: selectedOptions
                                            }))
                                        }
                                        options={appOptions}
                                        placeholder="Select apps"
                                        value={formState.appId}
                                        enableSelectAll
                                    />
                                </Box>

                                <Box style={{ flex: '1 1 360px' }}>
                                    <MultiSelectInput
                                        ariaLabel="Country"
                                        inputId="report-countries"
                                        isClearable
                                        label="Country"
                                        menuPortalTarget={menuPortalTarget}
                                        onChange={(selectedOptions) =>
                                            setFormState((currentState) => ({
                                                ...currentState,
                                                country: selectedOptions
                                            }))
                                        }
                                        options={countryOptions.map((item) => ({ label: item.label, value: item.code }))}
                                        placeholder="Select countries"
                                        value={formState.country}
                                        enableSelectAll
                                    />
                                </Box>
                            </Inline>

                            <Inline space="space.200" shouldWrap>
                                <Box style={{ flex: '1 1 360px' }}>
                                    <MultiSelectInput
                                        ariaLabel="Ad Unit Type"
                                        inputId="report-ad-unit-types"
                                        isClearable
                                        label="Ad Unit Type"
                                        menuPortalTarget={menuPortalTarget}
                                        onChange={(selectedOptions) =>
                                            setFormState((currentState) => ({
                                                ...currentState,
                                                adUnitType: selectedOptions
                                            }))
                                        }
                                        options={adUnitTypeOptions}
                                        placeholder="Select ad unit types"
                                        value={formState.adUnitType}
                                        enableSelectAll
                                    />
                                </Box>

                                <Box style={{ flex: '1 1 360px' }}>
                                    <MultiSelectInput
                                        ariaLabel="Platform"
                                        inputId="report-platforms"
                                        isClearable
                                        label="Platform"
                                        menuPortalTarget={menuPortalTarget}
                                        onChange={(selectedOptions) =>
                                            setFormState((currentState) => ({
                                                ...currentState,
                                                platform: selectedOptions
                                            }))
                                        }
                                        options={platformOptions}
                                        placeholder="Select platforms"
                                        value={formState.platform}
                                        enableSelectAll
                                    />
                                </Box>
                            </Inline>

                            {(userType===userTypes.Crackle_Admin || userType===userTypes.Crackle_User) && <Box style={{ maxWidth: 'min(calc(50% - 8px), 100%)', minWidth: 'min(320px, 100%)', width: '100%' }}>
                                <SimpleTreeMultiSelectInput
                                    allowCrossSectionSelection
                                    ariaLabel="OrgId"
                                    inputId="report-org-id"
                                    isClearable
                                    label="OrgId"
                                    menuPortalTarget={menuPortalTarget}
                                    onChange={(selectedOptions) => {
                                        const nextSpId = selectedOptions.filter((option) =>
                                            supplyPartnerIdSet.has(option.value)
                                        );
                                        const nextDpId = selectedOptions.filter(
                                            (option) => !supplyPartnerIdSet.has(option.value)
                                        );

                                        setFormState((currentState) => ({
                                            ...currentState,
                                            spId: nextSpId,
                                            dpId: nextDpId
                                        }));
                                    }}
                                    placeholder="Select org IDs"
                                    sections={orgTreeSections}
                                    value={selectedOrgIds}
                                />
                            </Box>}
                        </Stack>
                    </Box>

                    <Inline alignInline="end" space="space.150" shouldWrap>
                        <Button appearance="primary" onClick={() => void handleSubmit(0)}>
                            Save
                        </Button>
                        <Button appearance="primary" onClick={() => void handleSubmit(1)}>
                            Download
                        </Button>
                        <Button appearance="primary" onClick={() => void handleSubmit(2)}>
                            Save & Download
                        </Button>
                    </Inline>
                </Stack>
            </Box>
        </Box>,
        document.body
    );
}
